import { useSyncExternalStore } from 'react';
import { reviewSendOutcome, type ReviewComment } from './site-review';
import type { SiteMoveOffer, SiteMoveSeeds } from './presence-checklist';
import type { WorkspaceWebsite } from './workspace-website';

// The client-side state of "Edit site" and the in-app site review (contract: VMR
// `individual/docs/2026-10-24-site-edit-and-review-contract.md`; the shared client spec is mirrored by iOS
// `SiteReview.swift` and Android `SiteReview.kt`). Nothing here reaches a server: the review is collected
// client-side and leaves only as the one composer message the owner sends.
//
//   - SITE-CHAT BINDINGS: which conversation is about which Symphony site (conversationId → { workspaceId,
//     previewUrl }). Registered when Edit opens a Symphony site's chat, or when Wixel's "Edit in Symphony" link opens
//     its build chat on a computer (`lib/site-edit-arrival.ts`); read by the chat's "Preview site" link and by
//     a phone's Finish (which goes back to that chat). Persisted (localStorage) as a convenience only.
//   - the PREVIEW PANE request (a computer's live site beside the chat);
//   - the REVIEW comments, per site and conversation (`reviewKey`), in memory only;
//   - the phone REVIEW SHEET and the TAKEOVER / MOVE offer, opened from the Space's presence;
//   - a COMPOSER DRAFT request: text for the chat composer that is NOT sent (the hand-over ZIP);
//   - the review being SENT: Finish sends it to the site's chat, and its comments are cleared only once it landed.

type Listener = () => void;

function store<T>(initial: T) {
  let value = initial;
  const listeners = new Set<Listener>();
  return {
    get: () => value,
    set: (next: T) => {
      value = next;
      for (const l of [...listeners]) l();
    },
    subscribe: (l: Listener) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
  };
}

function useStore<T>(s: { get: () => T; subscribe: (l: Listener) => () => void }, server: T): T {
  return useSyncExternalStore(s.subscribe, s.get, () => server);
}

/* --------------------------------------------------------------------------------------- site-chat bindings */

export interface SiteChatBinding {
  workspaceId: string;
  previewUrl: string;
  /** When it was bound (ms), for the cap and for "the newest chat about this site". */
  at: number;
}

export const SITE_CHAT_BINDINGS_KEY = 'symphony.siteChatBindings';
export const SITE_CHAT_BINDINGS_MAX = 50;

/** `bindings` with `conversationId` bound, keeping at most `max` entries (the oldest dropped). Pure. */
export function withBinding(
  bindings: Record<string, SiteChatBinding>,
  conversationId: string,
  binding: SiteChatBinding,
  max = SITE_CHAT_BINDINGS_MAX,
): Record<string, SiteChatBinding> {
  const next = { ...bindings, [conversationId]: binding };
  const ids = Object.keys(next).sort((a, b) => next[b].at - next[a].at);
  return Object.fromEntries(ids.slice(0, max).map((id) => [id, next[id]]));
}

/** The newest conversation bound to this site in this Space, or null. Pure. */
export function newestBoundConversation(
  bindings: Record<string, SiteChatBinding>,
  workspaceId: string,
  previewOrigin: string,
): string | null {
  let best: { id: string; at: number } | null = null;
  for (const [id, b] of Object.entries(bindings)) {
    let origin = '';
    try {
      origin = new URL(b.previewUrl).origin;
    } catch {
      continue;
    }
    if (b.workspaceId === workspaceId && origin === previewOrigin && (!best || b.at > best.at)) best = { id, at: b.at };
  }
  return best?.id ?? null;
}

function readBindings(): Record<string, SiteChatBinding> {
  try {
    const raw = typeof window === 'undefined' ? null : window.localStorage.getItem(SITE_CHAT_BINDINGS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, SiteChatBinding> = {};
    for (const [id, b] of Object.entries(parsed as Record<string, Partial<SiteChatBinding>>)) {
      if (b && typeof b.workspaceId === 'string' && typeof b.previewUrl === 'string') out[id] = { workspaceId: b.workspaceId, previewUrl: b.previewUrl, at: Number(b.at) || 0 };
    }
    return out;
  } catch {
    return {};
  }
}

const bindings = store<Record<string, SiteChatBinding> | null>(null);
const allBindings = () => {
  const current = bindings.get();
  if (current) return current;
  const loaded = readBindings();
  bindings.set(loaded);
  return loaded;
};

export function bindSiteChat(conversationId: string, binding: Omit<SiteChatBinding, 'at'>): void {
  if (!conversationId || !binding.previewUrl) return;
  const next = withBinding(allBindings(), conversationId, { ...binding, at: Date.now() });
  bindings.set(next);
  try {
    window.localStorage.setItem(SITE_CHAT_BINDINGS_KEY, JSON.stringify(next));
  } catch {
    /* a convenience: an unwritable storage only loses it on reload */
  }
}

export function siteChatBinding(conversationId: string | null | undefined): SiteChatBinding | null {
  if (!conversationId) return null;
  return allBindings()[conversationId] ?? null;
}

export function boundConversationFor(workspaceId: string, previewUrl: string): string | null {
  let origin = '';
  try {
    origin = new URL(previewUrl).origin;
  } catch {
    return null;
  }
  return newestBoundConversation(allBindings(), workspaceId, origin);
}

/** The binding of `conversationId`, live. */
export function useSiteChatBinding(conversationId: string | null | undefined): SiteChatBinding | null {
  const all = useSyncExternalStore(bindings.subscribe, allBindings, () => null);
  return conversationId && all ? (all[conversationId] ?? null) : null;
}

/**
 * A Symphony site's chat that is about to be CREATED: Edit opens a new chat, whose id exists only once its first
 * send lands. `SiteChatAdopter` binds it then (and points the pane at it).
 */
const pendingSiteChat = store<{ workspaceId: string; previewUrl: string; at: number } | null>(null);
export const PENDING_SITE_CHAT_MS = 60_000;

export function expectSiteChat(workspaceId: string, previewUrl: string): void {
  pendingSiteChat.set({ workspaceId, previewUrl, at: Date.now() });
}

/** Binds the conversation that just became active to the site whose chat was expected, if one was. */
export function adoptSiteChat(conversationId: string | null): void {
  const pending = pendingSiteChat.get();
  if (!conversationId || !pending) return;
  pendingSiteChat.set(null);
  if (Date.now() - pending.at > PENDING_SITE_CHAT_MS) return;
  bindSiteChat(conversationId, { workspaceId: pending.workspaceId, previewUrl: pending.previewUrl });
  const pane = paneRequest.get();
  if (pane && pane.conversationId === null && pane.previewUrl === pending.previewUrl) paneRequest.set({ ...pane, conversationId });
}

/* ------------------------------------------------------------------------------------------ preview pane */

export interface SitePaneRequest {
  workspaceId: string;
  previewUrl: string;
  /** The conversation it is beside; null while that chat is still being created. */
  conversationId: string | null;
}

const paneRequest = store<SitePaneRequest | null>(null);

export function openSitePane(request: SitePaneRequest): void {
  paneRequest.set(request);
}

export function closeSitePane(): void {
  paneRequest.set(null);
}

export function useSitePaneRequest(): SitePaneRequest | null {
  return useStore(paneRequest, null);
}

/* ------------------------------------------------------------------------------------------- review state */

const reviews = store<Record<string, ReviewComment[]>>({});

export function useReviewComments(key: string): ReviewComment[] {
  const all = useStore(reviews, {});
  return all[key] ?? EMPTY;
}
const EMPTY: ReviewComment[] = [];

export function addReviewComment(key: string, comment: ReviewComment): void {
  const all = reviews.get();
  reviews.set({ ...all, [key]: [...(all[key] ?? []), comment] });
}

export function deleteReviewComment(key: string, index: number): void {
  const all = reviews.get();
  reviews.set({ ...all, [key]: (all[key] ?? []).filter((_, i) => i !== index) });
}

export function clearReview(key: string): void {
  const { [key]: _gone, ...rest } = reviews.get();
  reviews.set(rest);
}

export function reviewComments(key: string): ReviewComment[] {
  return reviews.get()[key] ?? EMPTY;
}

/* ------------------------------------------------------------------------------------- the review being sent */

interface PendingReviewSend {
  key: string;
  text: string;
  /** The chat it went to; null = a new chat (whose id does not exist yet). */
  conversationId: string | null;
  sawStreaming: boolean;
}

const pendingSend = store<PendingReviewSend | null>(null);
/** A send the chat never started (a dropped hand-off) releases Finish again after this long; the comments stay. */
export const REVIEW_SEND_START_MS = 30_000;

/** Finish: the review under `key` is on its way as `text`. Its comments stay until `observeReviewSend` sees it land. */
export function beginReviewSend(key: string, text: string, conversationId: string | null): void {
  const pending: PendingReviewSend = { key, text, conversationId, sawStreaming: false };
  pendingSend.set(pending);
  setTimeout(() => {
    const now = pendingSend.get();
    if (now && now.key === pending.key && now.text === pending.text && !now.sawStreaming) pendingSend.set(null);
  }, REVIEW_SEND_START_MS);
}

/** True while the review under `key` is being sent: Finish is disabled (no double send). */
export function useReviewSending(key: string): boolean {
  return useStore(pendingSend, null)?.key === key;
}

/**
 * The open chat reports its state; a pending review send settles off it. Sent: the comments are cleared. Failed: they
 * stay (the chat shows its own send error), and Finish works again.
 */
export function observeReviewSend(chat: { conversationId: string | null; streaming: boolean; userTexts: readonly string[] }): void {
  const pending = pendingSend.get();
  if (!pending || (pending.conversationId !== null && pending.conversationId !== chat.conversationId)) return;
  const sawStreaming = pending.sawStreaming || chat.streaming;
  const outcome = reviewSendOutcome({ text: pending.text, userTexts: chat.userTexts, streaming: chat.streaming, sawStreaming });
  if (outcome === 'pending') {
    if (sawStreaming !== pending.sawStreaming) pendingSend.set({ ...pending, sawStreaming });
    return;
  }
  pendingSend.set(null);
  if (outcome === 'sent') clearReview(pending.key);
}

/* ------------------------------------------------------------------------ the phone sheet and the offers */

/** Where a Space's presence action came from: enough to open that Space's chat about its site. */
export interface SiteSpace {
  workspaceId: string;
  workspaceName: string;
  website: WorkspaceWebsite | null;
  /** The site's address for the edit seed (its url, else its name). */
  siteRef: string;
}

export type SiteEditFlow =
  | ({ kind: 'review'; previewUrl: string } & SiteSpace)
  | ({ kind: 'takeover' } & SiteSpace)
  | ({ kind: 'move'; offer: SiteMoveOffer & SiteMoveSeeds } & SiteSpace);

const flow = store<SiteEditFlow | null>(null);

export function openSiteEditFlow(next: SiteEditFlow): void {
  flow.set(next);
}

export function closeSiteEditFlow(): void {
  flow.set(null);
}

export function useSiteEditFlow(): SiteEditFlow | null {
  return useStore(flow, null);
}

/* ------------------------------------------------------------------------------------------ composer draft */

export interface ComposerDraftRequest {
  id: number;
  text: string;
  /** The conversation whose composer takes it; null = the NEW chat. */
  conversationId: string | null;
  /** A new chat's Space, stamped on it when the owner sends (`metadata.workspaceId`). */
  workspaceId?: string;
  /** Open the composer's file picker too (the TAKEOVER ZIP). */
  attach?: boolean;
}

const draft = store<ComposerDraftRequest | null>(null);
let draftSeq = 0;

/** Puts `text` in the composer of `conversationId` (null = a new chat) WITHOUT sending it. */
export function requestComposerDraft(request: Omit<ComposerDraftRequest, 'id'>): void {
  draftSeq += 1;
  draft.set({ ...request, id: draftSeq });
}

export function consumeComposerDraft(id: number): void {
  if (draft.get()?.id === id) draft.set(null);
}

export function useComposerDraftRequest(): ComposerDraftRequest | null {
  return useStore(draft, null);
}

/** The composer's text after a draft lands: the draft alone, or appended after a blank line to what is typed. Pure. */
export function mergeDraft(typed: string, text: string): string {
  return typed.trim() ? `${typed.replace(/\s+$/, '')}\n\n${text}` : text;
}

/* ------------------------------------------------------------------------------------------ turn finished */

const turnListeners = new Set<(conversationId: string) => void>();

/** The open chat's turn just ended (streaming → idle). The preview pane reloads on its bound conversation's. */
export function reportChatTurnFinished(conversationId: string | null): void {
  if (!conversationId) return;
  for (const l of [...turnListeners]) l(conversationId);
}

export function onChatTurnFinished(listener: (conversationId: string) => void): () => void {
  turnListeners.add(listener);
  return () => {
    turnListeners.delete(listener);
  };
}
